import { getJSON } from "./api";
import type { ChatMessage } from "@sill/shared/chat/types";
import type { ProRenderPayload } from "./pro-agent-types";

// Rehydration of the copilot dock across page reloads (plan 188 follow-up).
// The pro assistant persists conversations in the same tables as consumer
// chat; `GET /api/professional/assistant/conversations/active` is the
// pro-gated twin of the consumer `/api/agent/conversations/active` read.

interface ProHistoryMessage {
  id: string;
  role: "user" | "advisor" | "assistant";
  text?: string;
  renders?: unknown[];
  profile_snapshot_id?: string;
  profile_recency_token?: string;
  followups?: string[];
  created_at: string;
}

interface ProHistoryResponse {
  conversation_id: string | null;
  messages: ProHistoryMessage[];
}

// A persisted render is any object carrying a string `type` discriminator —
// the RenderCard registry narrows on it, so no parsing is needed here.
function isRenderPayload(value: unknown): value is ProRenderPayload {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { type?: unknown }).type === "string"
  );
}

function toChatMessage(m: ProHistoryMessage): ChatMessage<ProRenderPayload> {
  const renders = (m.renders ?? []).filter(isRenderPayload);
  return {
    id: m.id,
    // The store + bubble styling only know "user" | "advisor"; the server
    // may persist the assistant role as "assistant".
    role: m.role === "user" ? "user" : "advisor",
    text: m.text,
    renders: renders.length > 0 ? renders : undefined,
    render: renders.length > 0 ? renders[renders.length - 1] : undefined,
    followups: m.followups,
    createdAt: m.created_at,
    profileSnapshotId: m.profile_snapshot_id,
    profileRecencyToken: m.profile_recency_token,
  };
}

export interface ProConversationHistory {
  conversationId: string | null;
  messages: ChatMessage<ProRenderPayload>[];
}

/**
 * Fetches the caller's most recent active copilot conversation so the dock
 * can rehydrate after a reload. Returns `{conversationId: null, messages: []}`
 * when the pro has no prior chat — callers treat that as the empty state.
 */
export async function fetchActiveProConversation(
  token: string,
  signal?: AbortSignal,
): Promise<ProConversationHistory> {
  const res = await getJSON<ProHistoryResponse>(
    "/api/professional/assistant/conversations/active",
    token,
    signal,
  );
  return {
    conversationId: res.conversation_id,
    messages: (res.messages ?? []).map(toChatMessage),
  };
}
